/**
 * Utility functions for widget scheduling/conditional display
 */

interface WeekdaySchedule {
  startTime?: string;
  endTime?: string;
}

interface SundayTimeRange {
  startTime?: string;
  endTime?: string;
}

interface WidgetSchedule {
  displayMode?: "always" | "scheduled";
  weekdaySchedule?: WeekdaySchedule;
  sundaySchedule?: "off" | "custom";
  sundayTimeRange?: SundayTimeRange;
}

/**
 * Converts time string (HH:MM) to minutes since midnight
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Checks if current time is within the given time range
 */
function isTimeInRange(
  currentMinutes: number,
  startTime?: string,
  endTime?: string
): boolean {
  if (!startTime || !endTime) return true;

  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);

  // Handle time ranges that cross midnight
  if (endMinutes < startMinutes) {
    return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
  }

  return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
}

/**
 * Checks if widget should be displayed based on schedule
 */
export function shouldDisplayWidget(schedule?: WidgetSchedule): boolean {
  // Default: always display
  if (!schedule || schedule.displayMode === "always") {
    return true;
  }

  // If scheduled mode but no schedule data, default to showing
  if (schedule.displayMode !== "scheduled") {
    return true;
  }

  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const currentHours = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTimeInMinutes = currentHours * 60 + currentMinutes;

  // Sunday handling
  if (currentDay === 0) {
    if (schedule.sundaySchedule === "off") {
      return false;
    }

    if (schedule.sundaySchedule === "custom" && schedule.sundayTimeRange) {
      return isTimeInRange(
        currentTimeInMinutes,
        schedule.sundayTimeRange.startTime,
        schedule.sundayTimeRange.endTime
      );
    }

    // If Sunday schedule is not properly configured, default to showing
    return true;
  }

  // Monday to Saturday (days 1-6)
  if (schedule.weekdaySchedule) {
    return isTimeInRange(
      currentTimeInMinutes,
      schedule.weekdaySchedule.startTime,
      schedule.weekdaySchedule.endTime
    );
  }

  // If weekday schedule is not configured, default to showing
  return true;
}

